import { getInboxConversations } from "../../services/whatsapp.service";
import {
  getCampaignReport,
  getCampaignReportRecipients,
} from "../../services/campaign.service";
import { buildCallCsvTable, buildConversationCsvTable, buildCsvContent } from "./csv";
import { getRowChannels, inboxApiChannel, inboundReportInboxChannel, uniqueReportRows, isPrimaryCallChannel } from "./helpers";
import { unwrapReportRows } from "./reportPayload";
import { INBOUND_AGENT_REPORT_MAX_ROWS } from "./reportLimits";

export { unwrapReportRows, parseCallReportPayload } from "./reportPayload";

const CONVERSATION_PAGE_SIZE = 100;
const CONVERSATION_MAX_PAGES = 1000;

export { buildCallCsvTable, buildConversationCsvTable };

export const downloadCsvFile = (csvContent: string, filename: string) => {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const csvFilename = (name: string, fallback = "report") => {
  const base = String(name || "").trim() || fallback;
  return `${base.replace(/[\\/:*?"<>|]+/g, "_")}.csv`;
};

export const mapCallExportRows = (rows: any[], inbound: boolean) =>
  (Array.isArray(rows) ? rows : []).map((item: any, index: number) => ({
    ...item,
    key: item.key || item._id || item.id || `row-${index}`,
    phone: inbound ? item.from || item.phone : item.to || item.phone,
    duration:
      item.call_duration !== undefined && item.call_duration !== null
        ? item.call_duration
        : item.duration,
  }));

export const mapConversationExportRows = (
  messages: any[],
  _options?: { id?: string; inbound?: boolean }
) => {
  return uniqueReportRows(Array.isArray(messages) ? messages : []).map(
    (item: any, index: number) => ({
      ...item,
      key: item._id || `convo-${index}`,
      phone: String(item.user_phone_number || item.phone || "").replace(
        /^whatsapp:/i,
        ""
      ),
    })
  );
};

export const downloadReportCsv = (
  table: unknown[][],
  filename: string
) => {
  downloadCsvFile(buildCsvContent(table), filename);
};

const conversationChannelsFor = (row: any): string[] => {
  const channels = getRowChannels(row)
    .map((channel) => inboxApiChannel(channel))
    .filter((channel): channel is string => Boolean(channel));
  return channels.length
    ? channels.filter((channel, index) => channels.indexOf(channel) === index)
    : ["WHATSAPP_MESSAGE"];
};

export const fetchAllCallExportRows = async (payload: {
  campaignId?: string;
  agentId?: string;
  keyword?: string;
  direction?: string;
  sort?: Record<string, 1 | -1>;
}) => {
  const response = await getCampaignReport({
    ...payload,
    exportAll: true,
  });
  const rows = unwrapReportRows(response.data);
  if (payload.agentId && !payload.campaignId) {
    return rows.slice(0, INBOUND_AGENT_REPORT_MAX_ROWS);
  }
  return rows;
};

const fetchConversationPage = async (
  channel: string,
  params: {
    page: number;
    limit: number;
    keyword?: string;
    agent_id?: string;
    campaign_id?: string;
    include_closed?: boolean;
  }
) => {
  const response = await getInboxConversations(channel, params);
  const body = response.data;
  const payload = body?.data ?? body;
  const messages = Array.isArray(payload?.messages)
    ? payload.messages
    : Array.isArray(payload)
    ? payload
    : [];
  const pagination = payload?.pagination || {};
  return { messages, pagination };
};

export const fetchAllConversationExportRows = async (options: {
  inbound: boolean;
  id: string;
  row?: any;
  keyword?: string;
}) => {
  const { inbound, id, row, keyword } = options;
  const channels = inbound
    ? [inboundReportInboxChannel(getRowChannels(row))]
    : conversationChannelsFor(row);
  const collected: any[] = [];

  for (const channel of channels) {
    for (let page = 1; page <= CONVERSATION_MAX_PAGES; page += 1) {
      const { messages, pagination } = await fetchConversationPage(channel, {
        page,
        limit: CONVERSATION_PAGE_SIZE,
        keyword,
        include_closed: true,
        ...(inbound ? { agent_id: id } : { campaign_id: id }),
      });
      collected.push(...messages);
      if (inbound && collected.length >= INBOUND_AGENT_REPORT_MAX_ROWS) {
        break;
      }
      const totalPages = Number(pagination.totalPages) || 1;
      if (page >= totalPages || messages.length < CONVERSATION_PAGE_SIZE) break;
    }
    if (inbound && collected.length >= INBOUND_AGENT_REPORT_MAX_ROWS) break;
  }

  const rows = inbound
    ? collected.slice(0, INBOUND_AGENT_REPORT_MAX_ROWS)
    : collected;
  return mapConversationExportRows(rows, { id, inbound });
};

export const fetchOutboundRecipientExportRows = async (campaignId: string) => {
  const response = await getCampaignReportRecipients(campaignId, {
    exportAll: true,
  });
  return mapConversationExportRows(unwrapReportRows(response.data));
};

export const shouldExportCallRows = (row: any, callRows: any[]) =>
  callRows.length > 0 || isPrimaryCallChannel(row);
